/**
 * **选中节点 / 连线的右键菜单** —— 从 `composables/useMenus.ts` 向下拆出来的子模块。
 *
 * ## 为什么单独一个文件
 *
 * `useMenus.ts` 原本是**一个 1100 行的函数**，三套菜单塞在一起。
 * 这一套（右键选中节点）是最大的一块（约 330 行），而它和另外两套的
 * 依赖**交集为 0** —— 所以按耦合拆开，上游只把需要的能力喂进来。
 *
 * ## 断言在哪
 *
 * 菜单结构（图标行 / 分割组 / 对齐工具同一行 / 「添加节点」不再是二级菜单 …）
 * 在 `scripts/mount-app.ts` 里是**真的挂 DOM、真的打开右键菜单去数**。
 * 所以搬动有没有搬坏，`npm run check` 说了算。
 *
 * ## ⚠️ 保持引用稳定
 *
 * `run: ctx.doUndo`（**不包箭头函数**）—— 这些回调来自宿主、引用本来稳定，
 * 包一层 `() => ctx.doUndo()` 会让每次重建菜单都换一个新函数。
 *
 * ## 归属
 *
 * **向下**：`useMenus.ts` → `menus/contextSections.ts`，只有前者引用它。
 */
import { computed, nextTick } from 'vue'
import type { ComputedRef } from 'vue'
/* ⚠️ 结构类型，不是引擎的类型 —— 换引擎时这一行不用改（`flowTypes.ts`） */
import type { FlowEdge as Edge } from '@/graph/flowTypes'
import { ASSETS } from '@/playground/nodeTypes'
import { arrangeRows } from '@/graph/arrangeTools'
import { MIN_NODE_WIDTH } from '@/theme/tokens'
import { ARRANGE_SIZE_MODES } from '@/graph/arrangeTools'
import { pruneMenu } from '@/types/menu'
import type { MenuSection } from '@/types/menu'
import type { AlignMode, SizeMode } from '@/graph/nodeAlign'
import type { GraphNodeModel } from '@/types/node'
import type { EdgeView } from '../composables/useSubgraphLevels'
import type { MenuState } from '../composables/useMenus'

/**
 * 这套菜单需要宿主提供的能力 —— 每一项都对应 `MenusCtx` 里的一个字段。
 *
 * ⚠️ 这是**窄化**过的：`MenusCtx` 有 67 个字段，这一套只用到 30 个。
 * 窄化接口就是「按耦合拆」的具体做法 —— 子模块拿不到它不需要的东西，
 * 也就不会偷偷依赖上游的内部状态。
 */
export interface ContextMenuCtx {
  menu: { value: MenuState | null }
  contextEdgeId: { value: string | null }
  selectedIds: { value: string[] }
  edgeViews: () => EdgeView[]
  /** 连线视图（Vue Flow 的边，静音状态从这里读） */
  edges: { value: Edge[] }
  /** 某条连线是不是静音的 */
  edgeMuted: (edgeId: string) => boolean
  modelOf: (id: string) => GraphNodeModel | undefined
  historyState: { value: { canUndo: boolean; canRedo: boolean; undo?: string; redo?: string } }
  clipboardState: { value: { canCopy: boolean; canPaste: boolean } }
  /**
   * 「添加节点」菜单的分组（`useAddNode` 里的 computed）。
   *
   * ⚠️ **传函数不传值。** 这两个是 **computed**（装卸节点包会变），
   * 而 `makeContextSections` 是在组件 setup 时**调一次**的 ——
   * 传 `.value` 等于把那一刻的数组**冻住**，之后装包卸载都不会反映到菜单里。
   *
   * ⚠️ 我第一版就是传的 `.value`（快照）。同一份数据的另一条路径
   * （`menuSections` 里读 `addSections.value`）是活的，于是两条路
   * **看到的资产清单不一样** —— 这类「一半活一半死」最难查。
   */
  addSections: () => MenuSection[]
  /** 同 `addSections`：资产清单，也是 computed */
  assetSections: () => MenuSection[]
  openAddMenu: (at?: { x: number; y: number }) => void
  openAssetMenu: (at?: { x: number; y: number }) => void
  toFlowPoint: (p: { x: number; y: number }) => { x: number; y: number }
  insertRerouteOnEdge: (
    edge: { id?: string; source: string; sourceHandle?: string | null; target: string; targetHandle?: string | null },
    point: { x: number; y: number } | null,
  ) => void
  doUndo: () => void
  doRedo: () => void
  doCopy: () => void
  doPaste: () => void
  doCut: () => void
  doAlign: (mode: AlignMode) => void
  doResize: (mode: SizeMode) => void
  canAlign: (count: number, mode: AlignMode) => boolean
  canResize: (count: number, mode: SizeMode) => boolean
  commit: (label: string) => void
  updateNodeInternals: (nodeIds?: string[]) => void
  toggleHideUnused: () => void
  deleteSelectedNodes: () => void
  groupSelection: () => void
  ungroupSelection: () => void
  wrapSelectionInFrame: () => void
}

const ICONS = {
  undo: '<path d="M9 14 4 9l5-5"/><path d="M4 9h10a6 6 0 0 1 0 12h-3"/>',
  redo: '<path d="m15 14 5-5-5-5"/><path d="M20 9H10a6 6 0 0 0 0 12h3"/>',
  copy: '<rect x="9" y="9" width="12" height="12" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/>',
  paste: '<path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/>',
  cut: '<circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M20 4 8.12 15.88M14.47 14.48 20 20M8.12 8.12 12 12"/>',
  trash: '<path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2"/><path d="M19 6l-1 14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1L5 6"/><path d="M10 11v6M14 11v6"/>',
} as const

/** 读一下就给你当前该显示的右键菜单 */
export function makeContextSections(ctx: ContextMenuCtx): ComputedRef<MenuSection[]> {
  return computed<MenuSection[]>(() => {
  /*
   * 这里原来还有一行 `void historyTick.value` —— 那是让菜单跟着历史变化的依赖。
   * `historyTick` 跟着撤销 / 重做一起搬进 useUndoRedo 后，下面读 `ctx.historyState.value`
   * 已经隐含了同一条依赖，所以删掉这行（读不到它就是会少跟踪）。
   */
  const h = ctx.historyState.value
  const c = ctx.clipboardState.value
  const selected = ctx.selectedIds.value.length
  /*
   * 选中里有任意一个节点已经折叠了未连接端口 → 这一项显示成「显示未连接端口」。
   *
   * ⚠️ 「隐藏 / 显示」这种一条项管两个方向的写法，用户点之前不知道
   * 点的会是哪一个。按当前状态给一个**确定的**动词，点完会发生什么是可预期的。
   * 多个节点状态不一致时按「至少有一个是隐藏的」算 —— 此时点下去是**全部显示**，
   * 也就是朝「看得见」的方向走，不会让东西凭空消失。
   */
  /*
   * 选中了**恰好一个组** —— 「解组」只在那种时候出现。
   * 它和「打成组」是一对逆操作，两个同时灰着会让人猜哪个能点。
   */
  const selectedGroupModel = computed(() => {
    const ids = ctx.selectedIds.value
    if (ids.length !== 1) return null
    const model = ctx.modelOf(ids[0] ?? '')
    return model?.type === 'group' && model.graph ? model : null
  })

  const anyPortsHidden = computed(
    () => ctx.selectedIds.value.some((id) => Boolean(ctx.modelOf(id)?.hideUnused)),
  )
  /*
   * 对齐 / 分布的条目工厂。
   *
   * ⚠️ 这里**只**产出数据，图标由调用处贴上（`{ ...alignItem(...), icon }`）——
   * 因为图标是「呈现」，不该由判定「能不能用」的地方决定。
   */
  const alignItem = (mode: AlignMode, label: string) => ({
    id: `align-${mode}`,
    label,
    disabled: !ctx.canAlign(selected, mode),
    run: () => ctx.doAlign(mode),
  })

  /** 等宽 / 等高 */
  const sizeItem = (mode: SizeMode, label: string) => ({
    id: `size-${mode}`,
    label,
    disabled: !ctx.canResize(selected, mode),
    run: () => ctx.doResize(mode),
  })

  /*
   * ⚠️ 整个右键菜单按用户的要求重排过（学 Win11 + 浏览器 DevTools 的
   * flex 对齐小工具）：
   *
   *   [图标行] 撤销 重做 复制 粘贴 删除 剪切
   *   添加节点 ▸
   *   ─── 选中节点 ───            ← 分割组（两侧横线 + 居中文字）
   *   给选中节点新建组 / 新建框 / 隐藏未连接端口 / 恢复默认宽度
   *   ─── 排列 ───
   *   [对齐 6] [分布 2] [尺寸 2]   ← 三行图标按钮
   *
   * 设计取舍：
   *   · 高频、看一眼就知道的**动词**做成图标行（省 6 行高度、不用读字）；
   *   · 「添加节点」是唯一**创造**东西的操作，单独一段、紧跟图标行；
   *   · 其余按「对象」分段，段标题用分割线而不是左对齐小标题 ——
   *     它们是分隔符，不是分类名。
   */
  return [
    /*
     * 右键落在连线上时，「连线」这一组排最前 —— 跟点击对象最相关的东西先出现。
     * 它不用图标行：三条都是**针对这一条线**的句子，读句子比认图标快。
     */
    ...(ctx.contextEdgeId.value
      ? [
          {
            id: 'edge',
            title: '连线',
            titleStyle: 'divider' as const,
            items: [
              {
                id: 'edge-mute',
                label: ctx.edgeMuted(ctx.contextEdgeId.value) ? '取消静音这条连线' : '静音这条连线',
                run: () => {
                  const id = ctx.contextEdgeId.value
                  if (!id) return
                  ctx.commit('静音连线')
                  const muted = !ctx.edgeMuted(id)
                  ctx.edges.value = (
                    ctx.edges.value as unknown as { id?: string; muted?: boolean }[]
                  ).map((e) => (e.id === id ? { ...e, muted } : e)) as unknown as Edge[]
                },
              },
              {
                id: 'edge-reroute',
                label: '在此插入接通节点',
                // 双击线条也能做，但那个没法被发现
                hint: '双击',
                run: () => {
                  const id = ctx.contextEdgeId.value
                  const at = ctx.menu.value
                  if (!id) return
                  const target = (ctx.edgeViews() as unknown as {
                    id?: string
                    source: string
                    sourceHandle?: string | null
                    target: string
                    targetHandle?: string | null
                  }[]).find((e) => e.id === id)
                  if (target) {
                    ctx.insertRerouteOnEdge(
                      target,
                      at ? ctx.toFlowPoint({ x: at.x, y: at.y }) : null,
                    )
                  }
                },
              },
              {
                id: 'edge-delete',
                label: '删除这条连线',
                danger: true,
                run: () => {
                  const id = ctx.contextEdgeId.value
                  if (!id) return
                  ctx.commit('删除连线')
                  ctx.edges.value = (
                    ctx.edges.value as unknown as { id?: string }[]
                  ).filter((e) => e.id !== id) as unknown as Edge[]
                  nextTick(() => ctx.updateNodeInternals())
                },
              },
            ],
          },
        ]
      : []),

    /* ── ① 图标行：最常用的六个动词 ─────────────────────────── */
    {
      id: 'quick',
      kind: 'toolbar' as const,
      items: [
        {
          id: 'undo',
          label: h.undo ? `撤销「${h.undo}」` : '撤销',
          hint: 'Ctrl+Z',
          icon: ICONS.undo,
          disabled: !h.canUndo,
          run: ctx.doUndo,
        },
        {
          id: 'redo',
          label: h.redo ? `重做「${h.redo}」` : '重做',
          hint: 'Ctrl+Shift+Z',
          icon: ICONS.redo,
          disabled: !h.canRedo,
          run: ctx.doRedo,
        },
        {
          id: 'copy',
          label: selected > 1 ? `复制 ${selected} 个节点` : '复制节点',
          hint: 'Ctrl+C',
          icon: ICONS.copy,
          disabled: !c.canCopy,
          run: ctx.doCopy,
        },
        {
          id: 'paste',
          label: '粘贴节点',
          hint: 'Ctrl+V',
          icon: ICONS.paste,
          disabled: !c.canPaste,
          run: ctx.doPaste,
        },
        {
          id: 'cut',
          label: selected > 1 ? `剪切 ${selected} 个节点` : '剪切节点',
          hint: 'Ctrl+X',
          icon: ICONS.cut,
          disabled: !c.canCopy,
          run: ctx.doCut,
        },
        {
          id: 'delete',
          label: selected > 1 ? `删除 ${selected} 个节点` : '删除节点',
          hint: 'Delete',
          icon: ICONS.trash,
          danger: true,
          disabled: selected === 0,
          run: ctx.deleteSelectedNodes,
        },
      ],
    },

    /* ── ② 添加节点：唯一「创造东西」的操作，单独一段 ─────────── */
    {
      id: 'create',
      items: [
        {
          /*
           * ★ 不再展开成二级菜单，而是**开和 Shift+A 同一个可搜索菜单**。
           *
           * 原来是 `children: ctx.addSections.value` —— 一个二级菜单。
           * 问题有两个：
           *   ① **子菜单不渲染搜索框**（`searchable` 只加在顶层），
           *      于是从右键/长按进来的用户只能一项项滚；
           *   ② 触摸端没有 `Shift+A`，右键/长按是**唯一**入口 ——
           *      也就是说**手机上永远没有搜索**（真机上菜单已经 18 项）。
           *
           * ⚠️ `nextTick` 是必须的：`pick()` 先 emit `select`（→ 跑 `run`）
           * 再 emit `close`（→ `closeMenu` 把 `ctx.menu` 置空）。
           * 在 `run` 里同步开新菜单会被紧接着的 `closeMenu` 抹掉 ——
           * 表现是「点了添加节点什么都没发生」。延到下一 tick 才躲得开。
           */
          id: 'add-node',
          label: '添加节点',
          hint: 'Shift+A',
          run: () => {
            const at = ctx.menu.value ? { x: ctx.menu.value.x, y: ctx.menu.value.y } : undefined
            void nextTick(() => ctx.openAddMenu(at))
          },
        },
        /*
         * ★「添加资产」—— 组节点资产（搭好的子图，当单个节点用）。
         *
         * ⚠️ 单独一项而不是混进「添加节点」：两者是**不同类别的东西** ——
         * 节点是「一个零件」，资产是「一整套接好的东西」。
         * 混在一起的话，菜单里会出现一批名字长得像节点、但放下去是一整张图的项。
         *
         * 没有资产时**整项不显示** —— 一个点开是空菜单的入口比没有更糟。
         */
        ...(ctx.assetSections().length > 0
          ? [
              {
                id: 'add-asset',
                label: '添加资产',
                hint: `${ASSETS.length} 个`,
                run: () => {
                  const at = ctx.menu.value ? { x: ctx.menu.value.x, y: ctx.menu.value.y } : undefined
                  void nextTick(() => ctx.openAssetMenu(at))
                },
              },
            ]
          : []),
      ],
    },

    /* ── ③ 选中节点：段标题走分割线 ─────────────────────────── */
    {
      id: 'selection',
      title: '选中节点',
      titleStyle: 'divider' as const,
      items: [
        {
          id: 'group-selection',
          label: selected > 1 ? `给选中的 ${selected} 个节点新建组` : '给选中节点新建组',
          hint: 'Ctrl+G',
          disabled: selected === 0,
          run: ctx.groupSelection,
        },
        /*
         * ⚠️ 只有**恰好选中一个组**时才出现（不是灰着）——
         * 它和「新建组」是一对逆操作，两个同时灰着会让人猜「到底哪个能点」。
         */
        ...(selectedGroupModel.value
          ? [{ id: 'ungroup-selection', label: '解组（把内部摊到这一层）', hint: 'Ctrl+Shift+G', run: ctx.ungroupSelection }]
          : []),
        {
          id: 'wrap-frame',
          label: selected > 1 ? `给选中的 ${selected} 个节点新建框` : '给选中节点新建框',
          disabled: selected === 0,
          run: ctx.wrapSelectionInFrame,
        },
        {
          id: 'hide-unused',
          label: anyPortsHidden.value ? '显示未连接端口' : '隐藏未连接端口',
          hint: 'Ctrl+H',
          disabled: selected === 0,
          run: ctx.toggleHideUnused,
        },
        {
          id: 'reset-width',
          label: '恢复默认宽度',
          disabled: selected === 0,
          run: () => {
            ctx.commit('恢复默认宽度')
            for (const id of ctx.selectedIds.value) {
              const model = ctx.modelOf(id)
              if (model) model.width = MIN_NODE_WIDTH            }
            nextTick(() => ctx.updateNodeInternals(ctx.selectedIds.value))
          },
        },
      ],
    },

    /*
     * 「排列」的三组图标行 —— **由 `ARRANGE_GROUPS` 生成**，不在这里手写。
     *
     * ⚠️ 手写的问题是「加了一种对齐方式但忘了加进菜单」这类漏项，
     * 而那个错误只能靠用户发现。生成之后，`verify:arrange` 会断言
     * 每个 `AlignMode` / `SizeMode` **恰好出现一次**。
     */
    ...arrangeRows().map((row, index) => ({
      id: row.id,
      kind: 'toolbar' as const,
      /*
       * 「排列」这个分割组标题挂在**第一行**上，而不是单独开一个空 section。
       *
       * ⚠️ 空 section 会被 `pruneMenu` 丢掉（它的职责就是「不要渲染空壳」），
       * 于是「排列」那行字会凭空消失 —— 实测踩到过：图标行都在，
       * 但上面那行「排列」不见了，用户根本不知道这三排是干什么的。
       */
      ...(index === 0 ? { title: '排列', titleStyle: 'divider' as const } : {}),
      items: row.tools.map((tool) =>
        /* 尺寸类走 `sizeItem`（改 width/height），其余走 `alignItem`（改 position） */
        ARRANGE_SIZE_MODES.includes(tool.mode as SizeMode)
          ? { ...sizeItem(tool.mode as SizeMode, tool.label), icon: tool.icon }
          : { ...alignItem(tool.mode as AlignMode, tool.label), icon: tool.icon },
      ),
    })),
  ]
  })
}
